import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import omit from 'lodash-es/omit';
import { IAttachmentOption } from 'pages/Conversations/interfaces/IAttachmentOption';
import CurrencyInput from 'shared/components/CurrencyInput';
import usePersistedState from 'shared/hooks/usePersistedState';
import { PersistedStateKeys } from 'shared/enums';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { useTracking } from 'shared/providers/TrackingProvider';
import { HStack, VStack } from '@televet/kibble-ui/build/chakra';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Card } from '@televet/kibble-ui/build/components/Card';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import { ConversationEvents } from 'shared/providers/TrackingProvider/EventNames';
import { useAppDispatch } from 'state/hooks';
import { toggleInvoicePreviewModal } from 'pages/Conversations/state/conversationsSlice';

const FIFTEEN_MB_IN_BYTES = 15000000;

interface IAttachmentProps {
  attachment: IAttachmentOption;
  onAttachmentChange: (attachment: IAttachmentOption) => void;
  onAttachmentRemove: (attachment: IAttachmentOption) => void;
  setCurrentInvoiceId: (invoiceId: string) => void;
  invoiceAmountInputRef: React.MutableRefObject<HTMLInputElement | null>;
}

const InvoiceAttachmentInput = ({
  attachment,
  onAttachmentChange,
  setCurrentInvoiceId,
  invoiceAmountInputRef,
}: IAttachmentProps): JSX.Element => {
  const [isDiscoveredInvoiceDemoModeEnabled] = usePersistedState(
    PersistedStateKeys.IsDiscoveredInvoiceDemoModeEnabled,
    false,
  );
  const toast = useToast();
  const inputFileRef = useRef<HTMLInputElement | null>(null);
  const source = 'MessageComposerInvoiceAttachmentInput';
  const { isInvoicePreviewSupported } = useIntegrationsProvider();
  const { trackEvent } = useTracking();
  const dispatch = useAppDispatch();

  const attachmentInvoiceIds = useMemo(() => {
    return attachment.invoiceIds || [];
  }, [attachment]);

  const handleCurrencyInputChange = useCallback(
    (value: string): void => {
      const input = parseFloat(value);
      if (input < 0) return;

      let validationError: string | undefined = undefined;

      if (input < 0.5) {
        validationError = 'Payment amount must be greater or equal to $0.50';
      }
      if (input > 30000) {
        validationError = 'Payment amount must be less than $30,000';
      }

      onAttachmentChange({ ...attachment, invoiceAmount: value, validationError });
    },
    [attachment, onAttachmentChange],
  );

  const handleDocumentClick = useCallback(() => {
    if (inputFileRef?.current) {
      inputFileRef.current.click();
    }
  }, [inputFileRef]);

  const updateSelectedFiles = async (files: FileList | null): Promise<void> => {
    if (!files || !files[0]) return;
    const file = files[0];

    if (file.size > FIFTEEN_MB_IN_BYTES) {
      toast({
        status: 'error',
        title: 'File must not exceed 15 MB',
      });
      return;
    }

    onAttachmentChange({
      ...attachment,
      file,
      value: {
        ...attachment.value,
        filename: file.name,
      },
    });
  };

  const onDocumentRemove = useCallback(
    (event?: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
      event?.stopPropagation();
      const updatedAttachment = omit(attachment, ['file', 'value.filename']) as IAttachmentOption;
      onAttachmentChange(updatedAttachment);
    },
    [attachment, onAttachmentChange],
  );

  const onInvoiceRemove = useCallback(() => {
    onAttachmentChange({
      ...attachment,
      invoiceAmount: '',
      invoiceIds: [],
    });
  }, [onAttachmentChange, attachment]);
  const openInvoicePreview = useCallback(() => {
    trackEvent(ConversationEvents.InvoiceViewPreview, { source });
    // clear currentInvoiceId so that selected invoices in draft message are shown
    setCurrentInvoiceId('');
    dispatch(toggleInvoicePreviewModal({ isOpen: true }));
  }, [dispatch, setCurrentInvoiceId, trackEvent]);

  /**
   * File attachments are not stored in message drafts, so we remove the invoice document
   * if it's invalid after being loaded from local storage.
   *
   * TODO: Move invalid file removal (and perhaps a user prompt) to a central place
   */
  useEffect(() => {
    const isFileSelectionInvalid = !!attachment.file && (!attachment.file.name || !attachment.file.type);
    if (isFileSelectionInvalid) {
      onDocumentRemove();
    }
  }, [attachment, onDocumentRemove]);

  const currencyInputAmount = useMemo(() => attachment.invoiceAmount, [attachment.invoiceAmount]);
  const isFileSelected = useMemo(() => !!attachment.file, [attachment.file]);

  return (
    <VStack className="InvoiceAttachment__InputContainer" w="100%" data-testid="invoice-attachment-input-container">
      <CurrencyInput
        inputRef={invoiceAmountInputRef}
        value={currencyInputAmount || ''}
        readOnly={(attachment.invoiceIds && attachment.invoiceIds.length > 0) || attachment.isBalanceSelected}
        onChange={handleCurrencyInputChange}
        size="sm"
      />
      {isFileSelected ? (
        <Card className="InvoiceAttachment__LinkedDocument" size="xs" w="100%">
          <HStack justify="space-between">
            <HStack spacing="1">
              <Icon size="sm" name="form" />
              <VStack spacing={0} align="flex-start">
                <Text fontWeight="semibold" size="sm" as="span" isTruncated>
                  1 Document Attached
                </Text>
                <Text size="sm" as="span" variant="subtle" isTruncated>
                  {attachment.value.filename ?? ''}
                </Text>
              </VStack>
            </HStack>
            <Button
              className="InvoiceAttachment__Button--RemoveDocument"
              variant="ghostNeutral"
              size="xs"
              iconName="close"
              onClick={onDocumentRemove}
            />
          </HStack>
        </Card>
      ) : (
        <Button
          className="InvoiceAttachment__Button--AddDocument"
          data-testid="invoice-attachment-add-document-button"
          isFullWidth
          onClick={handleDocumentClick}
          variant="tertiary"
          size="xs"
          iconName="plus"
        >
          Add Document
        </Button>
      )}
      <TextInput
        className="InvoiceAttachment__HiddenInput"
        key={JSON.stringify(inputFileRef?.current?.files)} // Force a re-render when files change
        type="file"
        name="file"
        id="file-input"
        ref={inputFileRef}
        display="none"
        onChange={({ target: { files } }): Promise<void> => updateSelectedFiles(files)}
      />
      {((attachmentInvoiceIds.length > 0 && isInvoicePreviewSupported) ||
        (isDiscoveredInvoiceDemoModeEnabled && parseFloat(attachment.invoiceAmount || '0') > 0)) && (
        <Card className="InvoiceAttachment__LinkedInvoice" size="xs" w="100%">
          <HStack justify="space-between">
            <HStack spacing="1">
              <Icon size="sm" name="invoice" />
              <Text fontWeight="semibold" size="sm" as="span" isTruncated>
                {attachmentInvoiceIds.length > 1
                  ? `${attachmentInvoiceIds.length} Invoices Attached`
                  : '1 Invoice Attached'}
              </Text>
            </HStack>
            <Button
              className="InvoiceAttachment__Button--RemoveInvoice"
              variant="ghostNeutral"
              size="xs"
              iconName="close"
              onClick={onInvoiceRemove}
            />
          </HStack>
          <Button
            className="InvoiceAttachment__Button--ViewInvoice"
            ml={4}
            variant="secondarySubtle"
            onClick={openInvoicePreview}
            size="xs"
          >
            View Invoice
          </Button>
        </Card>
      )}
    </VStack>
  );
};

export default InvoiceAttachmentInput;
